import React, { createContext, useContext, useState, useCallback } from 'react';

const GameContext = createContext();

export const GameProvider = ({ children }) => {
  const [gameType, setGameType] = useState('');
  const [corrects, setCorrects] = useState({});
  const [errors, setErrors] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeFinished, setTimeFinished] = useState(false);
  const [renderScore, setRenderScore] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const MAX_ATTEMPTS = 12;

  const [objectFound, setObjectFound] = useState(null);
  const [selectedObject, setSelectedObject] = useState(null);

  const handleObjectFound = useCallback(async (object) => {
    setSelectedObject(object);
  }, []);

  const handleScore = useCallback((timeLeft = 0) => {
    setIsPlaying(false);
    const correctCount = Object.keys(corrects).length;
    const errorCount = Object.keys(errors).length;
    
    // Calculate time multiplier based on remaining time
    // More time left = higher multiplier (max 2.0x, min 1.0x)
    const timeMultiplier = 1 + (timeLeft / 150); // 150 is the initial time (2.5 minutes)
    const clampedMultiplier = Math.min(2.0, Math.max(1.0, timeMultiplier));
    
    // Score based on correct answers, time bonus, and penalty for wrong answers
    let score = correctCount * 100; // Base score for correct answers
    score += errorCount * -20; // Penalty for wrong answers
    score = Math.round(score * clampedMultiplier); // Apply time multiplier
    
    setFinalScore(Math.max(0, score)); // Ensure score doesn't go below 0
    setRenderScore(true);
  }, [corrects, errors]);

  const incrementAttempt = useCallback(() => {
    setAttempts(prev => prev + 1);
  }, []);

  const resetAttempts = useCallback(() => {
    setAttempts(0);
  }, []);

  return (
    <GameContext.Provider
      value={{
        gameType,
        setGameType,
        corrects,
        setCorrects,
        errors,
        setErrors,
        isPlaying,
        setIsPlaying,
        timeFinished,
        setTimeFinished,
        renderScore,
        setRenderScore,
        finalScore,
        setFinalScore,
        handleObjectFound,
        handleScore,
        selectedObject,
        setSelectedObject,
        attempts,
        setAttempts,
        incrementAttempt,
        resetAttempts,
        MAX_ATTEMPTS,
      }}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};
